import { useQuery, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import { typedObjectEntries } from 'infrastructure/functions/typed-object-entries';

import type {
  ConversationType,
  IUnreadConversationsCount,
  IUnreadConversationsCountItem,
} from 'infrastructure/interfaces';

export const getSmsDashboardUnreadCountFn = async (): Promise<
  IUnreadConversationsCount | undefined
> => {
  const url = `core/conversations/unread-conversations-count`;

  const { data, error } = await dataService.getList<IUnreadConversationsCount>(
    apiUrlGenerator(url),
  );

  if (error) {
    showResult('Unable to retrieve unread conversations count');
    throw new Error(error);
  }

  return data;
};

export const useSmsDashboardUnreadCountKey = () => [
  'getSmsDashboardUnreadCount',
];

const conversationTypeToPropertyName: {
  [key in ConversationType]: keyof IUnreadConversationsCount;
} = {
  rpm: 'rpm',
  ccm: 'ccm',
  unregistered_rpm: 'rpmUnregistered',
  unregistered_ccm: 'ccmUnregistered',
};

export const initCounts: IUnreadConversationsCountItem = {
  read: 0,
  unread: 0,
  total: 0,
};

const initialData: IUnreadConversationsCount = {
  rpm: initCounts,
  ccm: initCounts,
  rpmUnregistered: initCounts,
  ccmUnregistered: initCounts,
};

interface IProps {
  canLoad?: boolean;
}

export const useSmsDashboardUnreadCount = (props?: IProps) => {
  const { canLoad = true } = props ?? {};
  const queryClient = useQueryClient();

  const {
    data: unreadConversationsCount,
    refetch,
    isFetching,
    isLoading,
    isRefetching,
    ...other
  } = useQuery({
    queryKey: useSmsDashboardUnreadCountKey(),
    queryFn: async () => getSmsDashboardUnreadCountFn(),
    initialData,
    enabled: canLoad,
  });

  const updateUnreadCountByType = (type: ConversationType, delta: number) => {
    const key = useSmsDashboardUnreadCountKey();
    queryClient.setQueryData<IUnreadConversationsCount>(key, (oldData) => {
      if (!oldData) return;

      const propertyName = conversationTypeToPropertyName[type];
      if (!propertyName) {
        throw new Error(`Invalid conversation type: ${type}`);
      }

      const currentCounts = oldData[propertyName] || initCounts;

      return {
        ...oldData,
        [propertyName]: {
          ...currentCounts,
          total: currentCounts.total + delta,
          unread: currentCounts.unread + delta,
        },
      };
    });
  };

  const updateUnreadCount = (data: IUnreadConversationsCount) => {
    const key = useSmsDashboardUnreadCountKey();
    queryClient.setQueryData<IUnreadConversationsCount>(key, (oldData) => {
      if (!oldData) return;

      return typedObjectEntries(data).reduce((acc, [k, v]) => {
        acc[k] = {
          ...acc[k],
          ...v,
        };
        return acc;
      }, initialData);
    });
  };

  return {
    unreadConversationsCount: unreadConversationsCount!,
    updateUnreadCountByType,
    updateUnreadCount,
    fetchUnreadConversationsCount: refetch,
    unreadConversationsCountLoading: isLoading || isFetching || isRefetching,
    isLoading,
    isFetching,
    ...other,
  };
};
